import { TFunction } from 'react-i18next';
import {
  BuildingTypes,
  OrderCancellationReason,
  OrderPaymentChannel,
  OrdersDeliveryType,
  OrdersPaymentStatus,
  OrdersSortDirection,
  OrdersStatus,
  OrdersUtmSource,
  PartnerConfirmationStatus,
} from '@/models/orders.model';

export const getDeliveryTypeOptions = (t: TFunction) => [
  // Hided for now
  // { value: OrdersDeliveryType.GOST, label: t('orders_delivery_type_gost') },
  { value: OrdersDeliveryType.NOVA_POSHTA, label: t('orders_delivery_type_nova_poshta') },
  { value: OrdersDeliveryType.PICKUP, label: t('orders_delivery_type_pickup') },
];

export const getStatusOptions = (t: TFunction, deliveryType?: OrdersDeliveryType) => {
  const options = [
    { value: OrdersStatus.NEW, label: t('orders_status_new') },
    { value: OrdersStatus.PROCESSING, label: t('orders_status_processing') },
    { value: OrdersStatus.TRANSFERRED, label: t('orders_status_transferred_option_name') },
    { value: OrdersStatus.ACCEPTED, label: t('orders_status_approved_option_name') },
    { value: OrdersStatus.READY_FOR_TTN, label: t('orders_status_ready_for_ttn') },
    { value: OrdersStatus.AWAITING, label: t('orders_status_awaiting_option_name') },
    { value: OrdersStatus.COMPLETED, label: t('orders_status_completed') },
    { value: OrdersStatus.CANCELLED, label: t('orders_status_cancelled') },
    { value: OrdersStatus.RESERVE, label: t('orders_status_reserve') },
  ];

  if (deliveryType && deliveryType !== OrdersDeliveryType.NOVA_POSHTA) {
    return options.filter((option) => option.value !== OrdersStatus.READY_FOR_TTN);
  }

  return options;
};

export const getCancellationReasonOptions = (t: TFunction) => [
  {
    value: OrderCancellationReason.CLIENT_NO_CONTACT_3_DAYS,
    label: t('cancellation_reason_client_no_contact_3_days'),
  },
  {
    value: OrderCancellationReason.RECIPIENT_REFUSED_NOVA_POSHTA,
    label: t('cancellation_reason_recipient_refused_nova_poshta'),
  },
  {
    value: OrderCancellationReason.PRODUCT_UNAVAILABLE_SUPPLIER,
    label: t('cancellation_reason_product_unavailable_supplier'),
  },
  {
    value: OrderCancellationReason.PRODUCT_MISMATCH_SPECS,
    label: t('cancellation_reason_product_mismatch_specs'),
  },
  {
    value: OrderCancellationReason.CLIENT_REFUSED_NO_REASON,
    label: t('cancellation_reason_client_refused_no_reason'),
  },
  {
    value: OrderCancellationReason.SUPPLIER_SHIPMENT_OVERDUE,
    label: t('cancellation_reason_supplier_shipment_overdue'),
  },
  {
    value: OrderCancellationReason.TEST_OR_DUPLICATE_ORDER,
    label: t('cancellation_reason_test_or_duplicate_order'),
  },
  {
    value: OrderCancellationReason.PRODUCT_UNAVAILABLE_GOST_PICKUP,
    label: t('cancellation_reason_product_unavailable_gost_pickup'),
  },
  {
    value: OrderCancellationReason.NOVA_POSHTA_DELIVERY_EXPENSIVE,
    label: t('cancellation_reason_nova_poshta_delivery_expensive'),
  },
  {
    value: OrderCancellationReason.ONLINE_PAYMENT_ERROR,
    label: t('cancellation_reason_online_payment_error'),
  },
  {
    value: OrderCancellationReason.SHIPMENT_DATES_NOT_SUITABLE,
    label: t('cancellation_reason_shipment_dates_not_suitable'),
  },
  {
    value: OrderCancellationReason.PRODUCT_NOT_SUITABLE_FOR_CLIENT,
    label: t('cancellation_reason_product_not_suitable_for_client'),
  },
  {
    value: OrderCancellationReason.ORDER_NO_LONGER_RELEVANT,
    label: t('cancellation_reason_order_no_longer_relevant'),
  },
  {
    value: OrderCancellationReason.CLIENT_REFUSED_PREPAYMENT,
    label: t('cancellation_reason_client_refused_prepayment'),
  },
  {
    value: OrderCancellationReason.MULTIPLE_TTN_NOT_SUITABLE,
    label: t('cancellation_reason_multiple_ttn_not_suitable'),
  },
  {
    value: OrderCancellationReason.INCORRECT_CLIENT_PHONE,
    label: t('cancellation_reason_incorrect_client_phone'),
  },
  {
    value: OrderCancellationReason.OTHER,
    label: t('cancellation_reason_other'),
  },
];

export const getPaymentStatusOptions = (t: TFunction) => [
  { value: OrdersPaymentStatus.AWAITING_PAYMENT, label: t('orders_payment_status_awaiting_payment') },
  { value: OrdersPaymentStatus.AWAITING_PREPAYMENT, label: t('orders_payment_status_awaiting_prepayment') },
  { value: OrdersPaymentStatus.PAID, label: t('orders_payment_status_paid') },
  { value: OrdersPaymentStatus.PREPAID, label: t('orders_payment_status_prepaid') },
  {
    value: OrdersPaymentStatus.AWAITING_ADDITIONAL_PAYMENT,
    label: t('orders_payment_status_awaiting_additional_payment'),
  },
  {
    value: OrdersPaymentStatus.REQUIRES_PARTIAL_REFUND,
    label: t('orders_payment_status_requires_partial_refund'),
  },
  { value: OrdersPaymentStatus.PAYMENT_REFUND, label: t('orders_payment_status_payment_refund') },
  { value: OrdersPaymentStatus.PREPAYMENT_REFUND, label: t('orders_payment_status_prepayment_refund') },
  { value: OrdersPaymentStatus.CANCELLED, label: t('orders_payment_status_cancelled') },
];

export const getPaymentStatusFilterOptions = (t: TFunction) => getPaymentStatusOptions(t);

export const getPaymentChannelOptions = (t: TFunction) => [
  { value: OrderPaymentChannel.CASH, label: t('orders_payment_channel_cash') },
  { value: OrderPaymentChannel.INTERNET_ACQUIRING, label: t('orders_payment_channel_internet_acquiring') },
  { value: OrderPaymentChannel.BANK_TRANSFER, label: t('orders_payment_channel_bank_transfer') },
  { value: OrderPaymentChannel.BANK_TERMINAL, label: t('orders_payment_channel_bank_terminal') },
  { value: OrderPaymentChannel.NOVAPAY, label: t('orders_payment_channel_novapay') },
];

export const getSortingOptions = (t: TFunction) => [
  { value: OrdersSortDirection.ASC, label: t('orders_sorting_asc_direction') },
  { value: OrdersSortDirection.DESC, label: t('orders_sorting_desc_direction') },
];

export const getUtmSourceOptions = (t: TFunction) => [
  { value: OrdersUtmSource.FACEBOOK, label: t('orders_utm_source_facebook') },
  { value: OrdersUtmSource.INSTAGRAM, label: t('orders_utm_source_instagram') },
  { value: OrdersUtmSource.GOOGLE, label: t('orders_utm_source_google') },
  { value: OrdersUtmSource.TELEGRAM, label: t('orders_utm_source_telegram') },
  { value: OrdersUtmSource.OTHERS, label: t('orders_utm_source_others') },
  { value: OrdersUtmSource.ORGANIC, label: t('orders_utm_source_organic') },
];

export const getPartnerConfirmationFilterOptions = (t: TFunction) => [
  {
    value: PartnerConfirmationStatus.CONFIRMED,
    label: t('orders_partner_confirmation_confirmed'),
  },
  {
    value: PartnerConfirmationStatus.PARTIAL,
    label: t('orders_partner_confirmation_partial'),
  },
  {
    value: PartnerConfirmationStatus.UNCONFIRMED,
    label: t('orders_partner_confirmation_not_confirmed'),
  },
];

export const buildingTypeOptions = (t: TFunction) => {
  return [
    { value: BuildingTypes.FLAT, label: t('order_flat') },
    { value: BuildingTypes.PRIVATE, label: t('order_private') },
  ];
};

export const elevatorOptions = (t: TFunction) => {
  return [
    { value: '1', label: t('order_elevator_yes') },
    { value: '0', label: t('order_elevator_no') },
  ];
};
